import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CheckCircle2, XCircle } from "lucide-react";

export default function ConfirmEmail() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking");
  const [message, setMessage] = useState("Confirming your new email…");
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    if (!token) { setStatus("error"); setMessage("Missing confirmation token."); return; }
    (async () => {
      try {
        const { data } = await api.post("/auth/confirm-email", { token });
        setStatus("ok");
        setMessage(`Your email has been updated to ${data.email}. Please sign in again with the new address.`);
        try {
          await api.post("/auth/logout");
        } catch (logoutErr) {
          // eslint-disable-next-line no-console
          console.warn("[confirm-email] auto-logout after email change failed:", logoutErr?.message || logoutErr);
        }
        await refresh();
      } catch (e) {
        setStatus("error");
        setMessage(formatApiError(e));
      }
    })();
  }, [token, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-6">
      <div className="w-full max-w-md card-warm p-10 text-center" data-testid="confirm-email-card">
        {status === "checking" && (
          <div className="mx-auto h-12 w-12 rounded-full bg-[color:var(--yellow)] animate-pulse mb-4" />
        )}
        {status === "ok" && (
          <div className="mx-auto h-14 w-14 rounded-full bg-[#E4EBDD] flex items-center justify-center text-[#2D4A3E] mb-4">
            <CheckCircle2 className="h-8 w-8" />
          </div>
        )}
        {status === "error" && (
          <div className="mx-auto h-14 w-14 rounded-full bg-[color:var(--red)]/15 flex items-center justify-center text-[color:var(--red)] mb-4">
            <XCircle className="h-8 w-8" />
          </div>
        )}
        <h1 className="font-display text-2xl mb-2">
          {status === "checking" ? "One moment" : status === "ok" ? "Email updated" : "That didn't work"}
        </h1>
        <p className="text-sm text-[color:var(--stone)]" data-testid="confirm-email-message">{message}</p>
        {status !== "checking" && (
          <button onClick={()=>nav("/login")} data-testid="confirm-email-login-btn" className="btn-pill btn-primary text-sm mt-6">Go to sign in</button>
        )}
      </div>
    </div>
  );
}
